import { For, Show, createMemo, type Component } from 'solid-js';
import { Text } from '~ui/text';
import { Table, type Column } from '~ui/table';
import { NA, formatPercent } from '~/lib/format';
import { bucketsFor, type Axis } from '~/stores/exposure-store';
import { lookThrough } from '~/data/look-through';
import { drawn, selected } from '~/stores/compare-store';
import { Swatch } from '~/features/swatch';

// What each fund is made of, on one axis, laid out the same way the charts are.
//
// Separate  each fund's own ranked list, side by side. Answers "what is this
//           fund" — the order is the fund's own, so its largest slice is first
//           whatever the others hold.
// Overlay   one row per bucket, one column per fund. Answers "how do these
//           differ" — every fund is read against the same rows, and a blank cell
//           is a fund that does not hold that sector at all.
//
// Weights are within each fund and never summed across them. Two funds both 20%
// Banks are 20% Banks each; what that comes to for a holder depends on how much
// of each they own, which is the Exposure page's question and not this one's.

interface Row {
    key: string;
    name: string;
    /** Present only on the company axis, and required for overlap — the cash and
        written-option lines match by name across every fund and would otherwise
        top the list of "things you own twice". */
    ticker: string | null;
    /** Weight within each selected fund, by symbol. Absent means not held. */
    byFund: Record<string, number>;
    /** Only for ordering the rows — see the note above on not summing. */
    total: number;
}

/**
 * Rows for the overlap view, built from the full look-through rather than from
 * what each fund publishes as its own holdings.
 *
 * Without it, comparing HDIV against HYLD reports that they both hold QMAX,
 * which is true and is not the question — they overlap on the companies inside
 * QMAX, and a comparison that stops at the wrapper says two funds are 5% alike
 * when they are 60% the same book.
 */
const SharedRows = (symbols: string[]): Row[] => {
    const rows = new Map<string, Row>();
    for (const symbol of symbols) {
        for (const leaf of lookThrough(symbol)) {
            if (leaf.ticker == null) continue;
            const row = rows.get(leaf.ticker) ?? { key: leaf.ticker, name: leaf.name, ticker: leaf.ticker, byFund: {}, total: 0 };
            row.byFund[symbol] = (row.byFund[symbol] ?? 0) + leaf.weight;
            row.total += leaf.weight;
            rows.set(leaf.ticker, row);
        }
    }
    return [...rows.values()].sort((a, b) => b.total - a.total);
};

const RowsFor = (axis: Axis, symbols: string[]): Row[] => {
    const rows = new Map<string, Row>();
    for (const symbol of symbols) {
        for (const bucket of bucketsFor(symbol, axis)) {
            const row = rows.get(bucket.key) ?? { key: bucket.key, name: bucket.name, ticker: bucket.ticker, byFund: {}, total: 0 };
            row.byFund[symbol] = (row.byFund[symbol] ?? 0) + bucket.weight;
            row.total += bucket.weight;
            rows.set(bucket.key, row);
        }
    }
    return [...rows.values()].sort((a, b) => b.total - a.total);
};

const Separate: Component<{ axis: Axis }> = (props) => (
    <div class='flex min-h-0 flex-1 gap-px bg-border'>
        <For each={selected()}>
            {(symbol) => {
                const buckets = createMemo(() => [...bucketsFor(symbol, props.axis)].sort((a, b) => b.weight - a.weight));
                return (
                    <div class='flex min-w-0 flex-1 flex-col bg-background'>
                        <div class='flex h-row shrink-0 items-center border-b px-2'>
                            <Text weight='medium'>{symbol}</Text>
                        </div>
                        <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
                            <Show when={buckets().length > 0} fallback={<Text tone='muted' class='block px-2 py-3 text-center'>Not published</Text>}>
                                <For each={buckets()}>
                                    {(b) => (
                                        <div class='flex h-row items-center justify-between gap-2 border-b px-2'>
                                            <Text class='truncate'>{b.name}</Text>
                                            <Text class='shrink-0'>{formatPercent(b.weight, 1)}</Text>
                                        </div>
                                    )}
                                </For>
                            </Show>
                        </div>
                    </div>
                );
            }}
        </For>
    </div>
);

const Overlaid: Component<{ axis: Axis; shared?: boolean }> = (props) => {
    const rows = createMemo(() => {
        const all = props.shared ? SharedRows(selected()) : RowsFor(props.axis, selected());
        // Held by more than one of the funds on screen. Not "owned twice" — that
        // is a fact about a book — but "these two funds are less different than
        // they look", which is the question you came to a comparison with.
        return props.shared ? all.filter((r) => Object.keys(r.byFund).length > 1) : all;
    });

    const columns = createMemo<Column<Row>[]>(() => [
        {
            id: 'name',
            header: '',
            size: 1.6,
            sortBy: (r) => r.name,
            cell: (r) => (
                <span class='flex min-w-0 items-baseline gap-2'>
                    <Show when={props.shared && r().ticker}>
                        <Text weight='medium' class='shrink-0'>
                            {r().ticker}
                        </Text>
                    </Show>
                    <Text class='truncate'>{r().name}</Text>
                </span>
            ),
        },
        ...drawn().map(
            ({ symbol, color, dash }): Column<Row> => ({
                id: symbol,
                // The same hue the fund carries on the overlay chart above, so a
                // column and a line are the same fund without having to be told.
                header: (
                    <span class='inline-flex items-center gap-1'>
                        <Swatch color={color} dash={dash} />
                        {symbol}
                    </span>
                ),
                align: 'right',
                size: 1,
                sortBy: (r) => r.byFund[symbol] ?? null,
                cell: (r) => <Text tone={r().byFund[symbol] == null ? 'muted' : undefined}>{r().byFund[symbol] == null ? NA : formatPercent(r().byFund[symbol], 1)}</Text>,
            }),
        ),
    ]);

    return <Table columns={columns()} rows={rows()} empty={props.shared ? 'No company is held by more than one of these funds' : 'No fund here publishes this breakdown'} />;
};

export const CompareBreakdown: Component<{ axis: Axis; overlay: boolean; shared?: boolean }> = (props) => (
    // Overlap is inherently a cross-fund question, so it ignores the
    // separate/overlay toggle — there is no per-fund version of "what do these
    // two have in common".
    <Show when={props.overlay || props.shared} fallback={<Separate axis={props.axis} />}>
        <Overlaid axis={props.axis} shared={props.shared} />
    </Show>
);

export default CompareBreakdown;
